import React, { ReactNode, useState } from 'react';
import { Alert } from '@/shared/ui';
import { getAddressDetails, getAddressPredictions } from '@/shared/helpers/address';
import { AdvancedAutoComplete } from '../autocomplete/adavanced-autoComplete';

interface AddressInputProps {
    uniqueIdentifier?: string;
    label?: string;
    subLabel?: string;
    boldLabel?: boolean;
    name?: string;
    placeholder?: string;
    required?: boolean;
    onInputBlur?: () => void;
    selectedAddress?: any;
    onInputChange?: (event: React.SyntheticEvent<Element, Event>) => void;
    onAddressSeleted?: (address: any) => void;
    hasDelete?: boolean;
    hasManualInput?: boolean;
    popover?: boolean;
    onPopoverClose?: () => void;
    onManualInput?(): void;
    error?: boolean;
    errorMessage?: string;
    alertClassName?: string;
}

export const AddressInput = ({
    uniqueIdentifier = 'address-input',
    required,
    label,
    subLabel,
    selectedAddress,
    onAddressSeleted,
    hasManualInput,
    onManualInput,
    placeholder,
    onInputChange,
    error,
    errorMessage,
    alertClassName = '',
}: AddressInputProps) => {
    const [inputValue, setInputValue] = useState(selectedAddress?.fullAddress || '');
    function parseAddressComponents(components) {
        const get = (types, lenType = "long_name") => {
            if (!Array.isArray(types)) types = [types];
            for (const type of types) {
                const comp = components.find(c => c.types.includes(type));
                if (comp) return comp[lenType];
            }
            return "";
        };

        return {
            streetNumber: get("street_number"),
            street: get("route"),
            city: get(["locality", "administrative_area_level_3", "postal_town"]),
            county: get("administrative_area_level_2"),
            state: get("administrative_area_level_1", "short_name"),
            country: get("country"),
            postalCode: get("postal_code"),
            subpremise: get("subpremise")
        };
    }

    const handlePlaceChange = async (option: { place_id: string }) => {
        if (option.place_id === 'MANUAL_INPUT') return onManualInput?.();

        const place = await getAddressDetails(option.place_id);
        const parsedAddress: any = parseAddressComponents(place?.address_components);
        const processed = {
            fullAddress: place?.formatted_address || '',
            addressLine1: `${parsedAddress.streetNumber || ''} ${parsedAddress.street || ''}`.trim(),
            addressLine2: '',
            city: parsedAddress.city,
            state: parsedAddress.state,
            zip: parsedAddress.postalCode,
            unit: parsedAddress.subpremise || '',
            verified: false,
            coordinates: {
                lat: place?.geometry?.location?.lat(),
                lng: place?.geometry?.location?.lng()
            }
        };

        onAddressSeleted?.(processed);
    };

    return (
        <div>
            <AdvancedAutoComplete<{ place_id: string; title?: string; description?: string; content?: ReactNode }>
                className="address_input"
                popoverInputVariant='advanced'
                popoverTitle="Search for your address"
                keepSelectedValue
                value={inputValue}
                onInput={onInputChange}
                onChange={setInputValue}
                needDict={false}
                uniqueIdentifier={uniqueIdentifier}
                required={required}
                label={label}
                subLabel={subLabel}
                placeholder={placeholder}
                labelKey="description"
                freeContentKey="content"
                loadOptionsList={getAddressPredictions}
                onSelect={handlePlaceChange}
                error={Boolean(error && errorMessage)}
                extraOptions={
                    hasManualInput
                        ? [
                            {
                                place_id: 'MANUAL_INPUT',
                                content: (
                                    <div className="flex flex-col">
                                        <span>Don't see your address?</span>
                                        <span className="text-primary-400">Add your address manually</span>
                                    </div>
                                )
                            }
                        ]
                        : []
                }
            />
            {error && errorMessage && <Alert className={`font-dmSans lg:mt-6 ${alertClassName}`} label={errorMessage} />}
        </div>
    );
};

export default AddressInput;
